import { useEffect, useState, useCallback, useRef } from "react";
import type { AffiliateStatusResponse } from "@codway-dev/shared";
import {
  AffiliateService,
  AffiliateRedeemError,
} from "../services/affiliate.service";

interface AffiliateState {
  status: AffiliateStatusResponse | null;
  loading: boolean;
  error: string | null;
  redeeming: boolean;
  redeemError: string | null;
}

interface AffiliateActions {
  redeem: (code: string) => Promise<void>;
}

export type UseAffiliateResult = AffiliateState & AffiliateActions;

// Friendly Spanish messages for each contract redeem error code.
const REDEEM_ERROR_MESSAGES: Record<string, string> = {
  CODE_NOT_FOUND: "El código no existe. Verifícalo e inténtalo de nuevo.",
  ALREADY_REDEEMED: "Ya tienes un código de afiliado registrado.",
  CODE_INACTIVE: "Este código ya no está activo.",
  CODE_EXPIRED: "Este código ha expirado.",
  CODE_MAX_USES_REACHED: "Este código alcanzó su número máximo de usos.",
};

function redeemErrorMessage(err: unknown): string {
  if (err instanceof AffiliateRedeemError) {
    return (
      REDEEM_ERROR_MESSAGES[err.code] ??
      "No pudimos registrar el código. Inténtalo de nuevo."
    );
  }
  return "No pudimos registrar el código. Inténtalo de nuevo.";
}

export function useAffiliate(): AffiliateState & AffiliateActions {
  const [state, setState] = useState<AffiliateState>({
    status: null,
    loading: true,
    error: null,
    redeeming: false,
    redeemError: null,
  });

  // Guards against setState after the welcome page unmounts mid-request (the
  // user navigates away while redeem/getStatus are still in flight).
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const data = await AffiliateService.getStatus();
        if (!cancelled) {
          setState((s) => ({ ...s, status: data, loading: false }));
        }
      } catch (err) {
        if (!cancelled)
          setState((s) => ({
            ...s,
            loading: false,
            error: err instanceof Error ? err.message : "Error desconocido",
          }));
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const redeem = useCallback(async (code: string) => {
    setState((s) => ({ ...s, redeeming: true, redeemError: null }));

    // Step 1 — the actual redemption. Only a failure HERE is a real redeem
    // error worth surfacing to the user.
    try {
      await AffiliateService.redeem(code);
    } catch (err) {
      if (mountedRef.current) {
        setState((s) => ({
          ...s,
          redeeming: false,
          redeemError: redeemErrorMessage(err),
        }));
      }
      throw err;
    }

    // Step 2 — refresh the full status so the redeemed view has every field
    // (periods, expiry, totals) the redeem response doesn't carry. This runs
    // AFTER a successful redemption, so a network failure here must NOT be
    // reported as a validation error — the code is already redeemed in the DB.
    try {
      const data = await AffiliateService.getStatus();
      if (mountedRef.current) {
        setState((s) => ({ ...s, redeeming: false, status: data }));
      }
    } catch {
      // Redemption succeeded; we just couldn't refresh the view. Drop the
      // spinner without an error — the next page load will reflect the status.
      if (mountedRef.current) {
        setState((s) => ({ ...s, redeeming: false }));
      }
    }
  }, []);

  return { ...state, redeem };
}
